// Guild War, phase 2 of guilds: a daily free-for-all of every registered
// guild, fully simulated on the server at 22:00 UTC. Pure rules shared by the
// server (server/guild-war.ts), the client and the tests: the clock, the
// score, the payouts and the battle itself. Numbers follow the reference game.
//
// The battle: every rostered player's war line-up (else their hunt team) is
// snapshotted at war time, fully healed, on the PvP ruleset (shared/pvp.ts)
// with the war's gentler level compression (^0.20 above Lv 150): moves by the
// auto-cast rule, cube-root rolls, fixed opening cooldowns. One of each
// species per player; a second copy sits out.
// Guilds stand on a ring. The top 4 by season GP spawn a quarter of the ring
// apart; the rest fill the gaps in a seeded random order.
//
// Targeting: every guild attacks the next guild still standing clockwise
// around the ring, and its creatures hit that guild's front three creatures.
// So at any moment each standing guild attacks exactly one guild and is
// attacked by exactly one, whatever its size or rank; when a guild falls, the
// ring closes up. Which of the front three a creature hits: element (best
// match-up for its moves), the only rule live wars use, since on the bench it
// beat front (the first standing), weakest (the least HP left) and spread
// (each attacker by its own place in line) (sim/TUNING_LOG.md, phases 6-7).
// The other rules stay for the bench and for replays: a stored war whose
// fighters carry no rule fought as spread, the only rule before phase 6. HP is multiplied by WAR.hpMult for the war only. Within a guild, the line is the players'
// first creatures in roster order, then their second creatures, and so on.
//
// Everything is drawn from one seed, so a war replays exactly from its stored
// snapshot and seed.
import { ARENA, arenaFighter, type ArenaCreature } from './arena.ts';
import { beatMs, kitMoves, moveScore, basicMove, type Fighter } from './combat.ts';
import { openingCooldowns, pvpCast } from './pvp.ts';
import { Rng } from './rng.ts';

export const WAR = {
  /** The war starts every day at this hour (UTC). */
  hourUtc: 22,
  /** Simulated time cap: guilds still standing are then ordered by remaining health. */
  capMs: 10 * 60_000,
  /** Simulation step. */
  stepMs: 100,
  /**
   * War-only HP multiplier (bosses do the same with BOSS_HP_MULT): a whole
   * guild hits a few creatures at once, so plain HP would end a war in
   * seconds. At 30 on the move engine a war lasts 1.5-6 simulated minutes
   * (battle design 3.7; sim/battle-bench.ts battle:warMinutes rows).
   */
  hpMult: 30,
  /** Each guild's attacks spread over the target guild's first this-many standing creatures. */
  frontSpread: 3,
  /** Fewer registered guilds than this: no war that day. */
  minGuilds: 2,
  /** Creatures per player. */
  teamMax: ARENA.teamMax,
  /** The top guilds by season GP that spawn far apart. */
  spreadTop: 4,
  /** Score: survival points over sqrt(fall position) plus combat points over sqrt(knockout rank). */
  survivalPts: 60,
  combatPts: 40,
  /** Daily GP-rank bonus for rostered players, in %, ranks 1-9; then tailBonus for any rank with GP above 0. */
  dailyBonus: [10, 9, 8, 7, 6, 5, 4, 3, 2],
  tailBonus: 1,
  /** Monthly season prize per rostered player, in diamonds, ranks 1-8 among qualifying guilds. */
  seasonDiamonds: [50, 25, 18, 12, 10, 8, 5, 3],
  /**
   * A guild qualifies in a war (daily bonus, and a share of the season prize)
   * only when at least this many of its rostered players fought, not admins,
   * at trainer level minPlayerLevel or higher.
   */
  minPlayers: 5,
  minPlayerLevel: ARENA.unlockLevel,
  /** Season prizes are paid only when at least this many guilds qualified in one war or more that season. */
  minPrizeGuilds: 10,
} as const;

// ---------- target rules (live wars use DEFAULT_WAR_TARGET; the rest are for the bench and old replays) ----------
export type WarTarget = 'spread' | 'front' | 'weakest' | 'element';
export const WAR_TARGETS: { id: WarTarget; name: string; desc: string }[] = [
  { id: 'element', name: 'Best match-up', desc: 'each of your creatures hits whichever of the front three its moves hit hardest by element' },
  { id: 'weakest', name: 'Finish the weakest', desc: 'hit whichever of the front three has the least HP left' },
  { id: 'front', name: 'Front first', desc: 'all your creatures hit the first creature still standing' },
  { id: 'spread', name: 'Spread', desc: 'each of your creatures hits the front creature matching its own place in line, so hits spread over the front three' },
];
/** A player who never picks a rule fights with this one (the bench's best). */
export const DEFAULT_WAR_TARGET: WarTarget = 'element';
/** A fighter with no rule in its snapshot (wars fought before the Battle Plan) used this one; replays keep it. */
export const LEGACY_WAR_TARGET: WarTarget = 'spread';
export const isWarTarget = (x: unknown): x is WarTarget => WAR_TARGETS.some((t) => t.id === x);

/** How hard an attacker's kit lands on a defender by element: its best move score (power × same element × chart). */
export function matchupScore(att: Fighter, def: Fighter): number {
  let best = moveScore(att, def, basicMove(att), 'war');
  for (const m of kitMoves(att)) best = Math.max(best, moveScore(att, def, m, 'war'));
  return best;
}

/**
 * Which of the target guild's front creatures (line indexes, in order) an attacker hits. place: the attacker's own
 * index in its guild's line; hp(k): HP left of line index k; score(k): matchupScore against it. Ties go to the earlier.
 */
export function pickTarget(rule: WarTarget, front: number[], place: number, hp: (k: number) => number, score: (k: number) => number): number {
  if (rule === 'front') return front[0];
  if (rule === 'weakest') { let b = front[0]; for (const k of front) if (hp(k) < hp(b)) b = k; return b; }
  if (rule === 'element') { let b = front[0]; for (const k of front) if (score(k) > score(b)) b = k; return b; }
  return front[place % front.length];
}

// ---------- the clock ----------
const DAY = 86_400_000;
/** The latest war start at or before `now` (22:00 UTC). */
export function warAt(now: number): number {
  const d = new Date(now);
  let t = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), WAR.hourUtc);
  if (t > now) t -= DAY;
  return t;
}
export const nextWarAt = (now: number) => warAt(now) + DAY;
/** A war's day: its UTC date, YYYY-MM-DD. */
export const warDay = (at: number) => new Date(at).toISOString().slice(0, 10);
/** The monthly season a war belongs to: YYYY-MM of its start (UTC). */
export const seasonOf = (at: number) => new Date(at).toISOString().slice(0, 7);
/** When the season running at `now` closes: the 1st of next month, 00:00 UTC. */
export function seasonEndAt(now: number): number {
  const d = new Date(now);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}

// ---------- the score ----------
export const round1 = (x: number) => Math.round(x * 10) / 10;
export const survivalOf = (fall: number) => WAR.survivalPts / Math.sqrt(fall);
export const combatOf = (koRank: number) => (koRank > 0 ? WAR.combatPts / Math.sqrt(koRank) : 0);

/** Knockout ranks: 1 = most knockouts, ties share a rank (1, 1, 3), no knockouts = 0 (no combat points). */
export function koRanks(kos: number[]): number[] {
  return kos.map((k) => (k > 0 ? 1 + kos.filter((x) => x > k).length : 0));
}

/**
 * One guild's score: survival and combat each rounded to one decimal, then
 * added. GP is the score rounded to a whole number with x.5 rounding down.
 * Worked in tenths so 12.5 is exactly 12.5.
 */
export function scoreOf(fall: number, koRank: number): { survival: number; combat: number; score: number; gp: number } {
  const s10 = Math.round(survivalOf(fall) * 10), c10 = Math.round(combatOf(koRank) * 10);
  return { survival: s10 / 10, combat: c10 / 10, score: (s10 + c10) / 10, gp: Math.floor((s10 + c10 + 4) / 10) };
}

/** The daily bonus in % for a GP rank: 10% for 1st down to 2% for 9th, then 1% for anyone with GP. */
export function dailyBonus(rank: number, gp: number): number {
  if (gp <= 0 || rank < 1) return 0;
  return rank <= WAR.dailyBonus.length ? WAR.dailyBonus[rank - 1] : WAR.tailBonus;
}

/** Diamonds per rostered player for a season rank (0 below 8th). */
export const seasonPrize = (rank: number) => (rank >= 1 ? WAR.seasonDiamonds[rank - 1] ?? 0 : 0);

/** The most a full season pays out: ranks 1-8 with full rosters. */
export function monthlyPrizeDiamonds(rosterSize = 10): number {
  return WAR.seasonDiamonds.reduce((a, b) => a + b, 0) * rosterSize;
}

export interface WarEntry { guild: number; fall: number; kos: number }
export interface WarStanding extends WarEntry { koRank: number; survival: number; combat: number; score: number; gp: number; rank: number; bonus: number }

/** A war's standings: score, then fell later, then more knockouts. Damage dealt doesn't count. */
export function rankWar(entries: WarEntry[]): WarStanding[] {
  const ranks = koRanks(entries.map((e) => e.kos));
  return entries
    .map((e, i) => ({ ...e, koRank: ranks[i], ...scoreOf(e.fall, ranks[i]) }))
    .sort((a, b) => b.score - a.score || a.fall - b.fall || b.kos - a.kos || a.guild - b.guild)
    .map((s, i) => ({ ...s, rank: i + 1, bonus: dailyBonus(i + 1, s.gp) }));
}

/** qwars: the wars this season in which the guild qualified (WAR.minPlayers players at Lv WAR.minPlayerLevel+). */
export interface SeasonRow { guild: number; gp: number; kos: number; wars: number; qwars: number }
/**
 * Season order: GP, then knockouts over the season, then the older guild.
 * Every guild is ranked; prizes go by place among the guilds that qualified
 * in at least one war, and only when WAR.minPrizeGuilds or more did.
 * prize is the full share: a player gets it × (qualified wars on the roster ÷ wars that season).
 */
export function seasonOrder<T extends SeasonRow>(rows: T[]): (T & { rank: number; prize: number; qualified: boolean })[] {
  const order = [...rows].sort((a, b) => b.gp - a.gp || b.kos - a.kos || a.guild - b.guild);
  const paying = prizesOn(rows);
  let q = 0;
  return order.map((r, i) => {
    const qualified = r.qwars > 0;
    const prize = qualified && paying && r.gp > 0 ? seasonPrize(++q) : 0;
    return { ...r, rank: i + 1, prize, qualified };
  });
}
/** Whether a season's prizes are paid: enough guilds qualified in at least one war. */
export const prizesOn = (rows: { qwars: number }[]) => rows.filter((r) => r.qwars > 0).length >= WAR.minPrizeGuilds;
/** A player's diamonds from a season prize: the full prize × qualified wars on the roster ÷ wars that season. */
export const prizeShare = (prize: number, wars: number, total: number) => (total > 0 ? Math.round((prize * Math.min(wars, total)) / total) : 0);

// ---------- the battle ----------
/** target: the owner's target rule. Absent = spread (LEGACY_WAR_TARGET), as in wars fought before the rule existed. */
export interface WarFighter { account: number; owner: string; c: ArenaCreature; target?: WarTarget }
export interface WarGuildIn { id: number; name: string; tag: string; seasonGp: number; fighters: WarFighter[] }

/** A creature as the log names it. */
export interface WarWho { g: number; owner: string; species: string; id: number }
/** ko: `by` knocked out `who`. fall: guild `g` has no creature left standing. */
export interface WarEvent { ms: number; k: 'ko' | 'fall'; g: number; by?: WarWho; who?: WarWho }
export interface WarGuildOut {
  id: number; name: string; tag: string; slot: number; fighters: number; maxHp: number; hpLeft: number;
  kos: number; dealt: number; fall: number; fellAt: number | null;
}
export interface WarSimResult {
  seed: number; ms: number;
  /** Guild ids by ring slot. */
  ring: number[];
  guilds: WarGuildOut[];
  log: WarEvent[];
  /** The creature that dealt the most damage (HP actually taken off), ties to the earlier one in ring and line order. */
  top: (WarWho & { account: number; dealt: number }) | null;
}

/** Ring slots: the top 4 by season GP a quarter of the ring apart, the rest shuffled into the gaps. */
export function placeGuilds(guilds: WarGuildIn[], rng: Rng): number[] {
  const n = guilds.length;
  const order = [...guilds].sort((a, b) => b.seasonGp - a.seasonGp || a.id - b.id);
  const k = Math.min(WAR.spreadTop, n);
  const rest = order.slice(k);
  for (let i = rest.length - 1; i > 0; i--) { const j = rng.int(0, i); [rest[i], rest[j]] = [rest[j], rest[i]]; }
  const ring: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < k; i++) ring[Math.floor((i * n) / k)] = order[i].id;
  let r = 0;
  for (let s = 0; s < n; s++) if (ring[s] === null) ring[s] = rest[r++].id;
  return ring as number[];
}

/** A guild's battle line: every player's first creature in roster order, then every second, and so on. */
export function lineOf(teams: WarFighter[][]): WarFighter[] {
  const out: WarFighter[] = [];
  const depth = Math.max(0, ...teams.map((t) => t.length));
  for (let i = 0; i < depth; i++) for (const t of teams) if (t[i]) out.push(t[i]);
  return out;
}

/** One of each species per player: a player's second copy of a species sits out (the first keeps its place). */
export function onePerSpecies(fighters: WarFighter[]): WarFighter[] {
  const seen = new Set<string>();
  return fighters.filter((f) => { const k = `${f.account}:${f.c.species}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

/**
 * A creature's first beat in the war, fixed by its place in line: 0.3-1.0 of a beat, spread by the golden ratio so
 * neighbours in line don't strike together (was a seeded draw over the same range before 2026-10-15).
 */
export function warFirstBeat(f: Fighter, lineIndex: number): number {
  const u = ((lineIndex + 0.5) * 0.6180339887) % 1;
  return beatMs(f) * (0.3 + 0.7 * u);
}

/** A whole war at once (tests, replays, the balance harness). */
export function simulateWar(guilds: WarGuildIn[], seed: number, hpMult: number = WAR.hpMult): WarSimResult {
  const w = startWar(guilds, seed, hpMult);
  w.advance(Infinity);
  return w.result();
}

/** A war in progress: the server advances it a few milliseconds per tick so the hunt keeps ticking. */
export interface WarRun {
  /** Simulate until done or until the clock (performance.now) passes `deadline`. True when the war is over. */
  advance(deadline: number): boolean;
  readonly done: boolean;
  /** The outcome; only once done. */
  result(): WarSimResult;
}

/**
 * Set up a war to simulate in slices. Slicing never changes the outcome: the
 * same guilds and seed give the same war however it is advanced.
 */
export function startWar(guilds: WarGuildIn[], seed: number, hpMult: number = WAR.hpMult): WarRun {
  const rng = new Rng(seed);
  const ring = placeGuilds(guilds, rng);
  const G = ring.map((id) => guilds.find((g) => g.id === id)!);
  const n = G.length;
  const sides = G.map((g) => onePerSpecies(g.fighters).map((f, li) => {
    const ft = arenaFighter(f.c, 'war');
    const hp = Math.max(1, Math.round(ft.stats.hp * hpMult));
    return { f, ft, hp, max: hp, next: warFirstBeat(ft, li), ready: openingCooldowns(ft, 0), dealt: 0, rule: f.target ?? LEGACY_WAR_TARGET, scores: new Map<number, number>() };
  }));
  /** Standing creatures per guild, and the line index of the first one still standing. */
  const aliveN = sides.map((x) => x.length);
  const first = sides.map(() => 0);
  /** The target guild's front: its first WAR.frontSpread standing creatures in line order. */
  const fronts = (j: number) => {
    const line = sides[j];
    while (first[j] < line.length && line[first[j]].hp <= 0) first[j]++;
    const out: number[] = [];
    for (let k = first[j]; k < line.length && out.length < WAR.frontSpread; k++) if (line[k].hp > 0) out.push(k);
    return out;
  };
  const kos = sides.map(() => 0);
  const fellAt: (number | null)[] = sides.map(() => null);
  const falls: number[] = [];
  const log: WarEvent[] = [];
  const standing = (i: number) => aliveN[i] > 0;
  const left = () => { let c = 0; for (let i = 0; i < n; i++) if (standing(i)) c++; return c; };
  /** The next standing guild clockwise. */
  const target = (i: number) => { for (let d = 1; d < n; d++) { const j = (i + d) % n; if (standing(j)) return j; } return -1; };
  const who = (i: number, x: (typeof sides)[number][number]): WarWho => ({ g: G[i].id, owner: x.f.owner, species: x.f.c.species, id: x.f.c.id });
  for (let i = 0; i < n; i++) if (!standing(i)) { fellAt[i] = 0; falls.push(i); }

  let t = 0, step = 0, alive = left();
  const over = () => !(t < WAR.capMs && alive >= 2);
  const advance = (deadline: number): boolean => {
    let did = 0;
    while (!over()) {
      // Check the clock every 20 steps (2 simulated seconds); every call makes some progress.
      if (deadline !== Infinity && ++did % 20 === 0 && performance.now() > deadline) return false;
      t += WAR.stepMs;
      // The guild that acts first in a step rotates, so no ring slot always strikes first.
      const start = step++ % n;
      for (let k = 0; k < n && alive >= 2; k++) {
        const i = (start + k) % n;
        if (!standing(i)) continue;
        for (let xi = 0; xi < sides[i].length; xi++) {
          const x = sides[i][xi];
          if (x.hp <= 0 || t < x.next) continue;
          const j = target(i);
          if (j < 0) break;
          // The owner's target rule picks one of the front creatures (spread: by the attacker's own place in line).
          const f = fronts(j);
          const line = sides[j];
          const k = x.rule === 'spread' ? f[xi % f.length] : pickTarget(x.rule, f, xi, (q) => line[q].hp, (q) => {
            const key = j * 100_000 + q;
            let v = x.scores.get(key);
            if (v === undefined) { v = matchupScore(x.ft, line[q].ft); x.scores.set(key, v); }
            return v;
          });
          const d = line[k];
          const h = pvpCast(x.ft, d.ft, x.ready, d.hp, t, rng, 'war');
          const took = Math.min(h.dmg, d.hp);
          d.hp -= took; x.dealt += took;
          x.next = t + beatMs(x.ft);
          if (d.hp > 0) continue;
          kos[i]++;
          log.push({ ms: t, k: 'ko', g: G[i].id, by: who(i, x), who: who(j, d) });
          aliveN[j]--;
          if (!standing(j)) {
            fellAt[j] = t; falls.push(j); alive--;
            log.push({ ms: t, k: 'fall', g: G[j].id });
            if (alive < 2) break;
          }
        }
      }
    }
    return true;
  };

  const result = (): WarSimResult => {
    if (!over()) throw new Error('war still running');
    // Fall positions: the first to fall is last (n); those standing at the end are ordered by health left (share of their total), then knockouts.
    const fall = new Array<number>(n).fill(0);
    falls.forEach((i, k) => { fall[i] = n - k; });
    const share = (i: number) => sides[i].reduce((a, x) => a + x.hp, 0) / Math.max(1, sides[i].reduce((a, x) => a + x.max, 0));
    const survivors = [...Array(n).keys()].filter((i) => standing(i)).sort((a, b) => share(b) - share(a) || kos[b] - kos[a] || G[a].id - G[b].id);
    survivors.forEach((i, k) => { fall[i] = k + 1; });

    let top: WarSimResult['top'] = null;
    for (let i = 0; i < n; i++) for (const x of sides[i]) if (x.dealt > 0 && (!top || x.dealt > top.dealt)) top = { ...who(i, x), account: x.f.account, dealt: x.dealt };

    return {
      seed, ms: t, ring, log, top,
      guilds: G.map((g, i) => ({
        id: g.id, name: g.name, tag: g.tag, slot: i, fighters: sides[i].length,
        maxHp: sides[i].reduce((a, x) => a + x.max, 0), hpLeft: sides[i].reduce((a, x) => a + x.hp, 0),
        kos: kos[i], dealt: sides[i].reduce((a, x) => a + x.dealt, 0), fall: fall[i], fellAt: fellAt[i],
      })),
    };
  };
  return { advance, result, get done() { return over(); } };
}

// ---------- messages ----------
/** Lines per page of a war's full battle log. */
export const WAR_LOG_PAGE = 200;
/** One page of a war's full battle log: lines from index `from`. */
export interface WarLogPage { no: number; page: number; pages: number; total: number; from: number; lines: WarEvent[] }
/** One guild's line in a war report. */
/** qualified/players: whether the guild qualified (absent on wars from before the rule), and how many players counted toward it. */
export type WarRow = WarGuildOut & WarStanding & { qualified?: boolean; players?: number };
export interface WarReport {
  /** The war's number (also the number on its medal). */
  no: number; at: number; season: string; seed: number; ms: number;
  /** In standings order. */
  rows: WarRow[];
  /**
   * The battle log as the Guild page loads it: only the lines that touch the
   * viewer's guild and every fall. The whole log (logTotal lines) comes a page
   * at a time with { t: 'guildWarLog', no, page }.
   */
  log: WarEvent[];
  /** Index of each line of `log` in the whole log. */
  logAt?: number[];
  logTotal?: number;
  medal: (WarWho & { dealt: number }) | null;
}
export interface WarSeasonRow { rank: number; guild: number; name: string; tag: string; gp: number; kos: number; wars: number; qwars: number; prize: number; mine?: boolean }
export interface GuildWarDTO {
  /** Your guild is registered (it enters every day). */
  registered: boolean;
  registeredCount: number;
  nextAt: number;
  season: string;
  seasonEndsAt: number;
  /** Your saved war line-up (creature ids); empty = your hunt team fights. */
  lineup: number[];
  /** Your daily bonus from the last war, while it lasts. */
  bonus: { pct: number; until: number } | null;
  /** The last war your guild fought in. */
  last: WarReport | null;
  /** This month's standings (top 20, plus your guild's row if lower). */
  standings: WarSeasonRow[];
  /** Guilds that qualified in at least one war this season, the number needed for prizes, and the wars run so far. */
  prizes: { qualified: number; need: number; wars: number };
  /** Your qualified wars on a roster this season (for your share of the prize). */
  myWars: number;
  /** Last month's result for your guild, if it placed. */
  lastSeason: { season: string; rank: number; gp: number; prize: number; paid?: number } | null;
}
